import React, { useEffect, useRef, useState, useCallback } from "react";
import { Download, Upload, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { downloadFile } from "@/lib/download";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const loadImg = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

export default function ImageEditor({ open, item, projectLogo, brandName, suggestedTexts = [], onClose, allowLogo = true, fileName = "image" }) {
  const canvasRef = useRef(null);
  const [text, setText] = useState("");
  const [color, setColor] = useState("#ffffff");
  const [size, setSize] = useState("44");
  const [position, setPosition] = useState("bottom");
  const [showLogo, setShowLogo] = useState(false);
  const [showName, setShowName] = useState(true);
  const [logoCorner, setLogoCorner] = useState("top-right");
  const [logoSrc, setLogoSrc] = useState(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    if (open && item) {
      setText(brandName || item.brand_name || item.title || "");
      setColor("#ffffff");
      setSize("44");
      setPosition("bottom");
      setShowName(allowLogo);  // اللوقو أصل نظيف — لا اسم ولا شعار آخر فوقه افتراضيًا
      const lg = projectLogo || null;
      setLogoSrc(lg);
      setShowLogo(false);
      setLogoCorner("top-right");
    }
  }, [open, item, projectLogo, brandName, allowLogo]);

  const redraw = useCallback(async () => {
    if (!open || !item?.data) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDrawing(true);
    try {
      try { await document.fonts.load(`${size}px Tajawal`); } catch (e) {}
      const base = await loadImg(`data:image/png;base64,${item.data}`);
      let w = base.naturalWidth || 1024;
      let h = base.naturalHeight || 1024;
      const cap = 1280;
      const scale = Math.min(1, cap / Math.max(w, h));
      w = Math.round(w * scale); h = Math.round(h * scale);
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(base, 0, 0, w, h);

      if (showLogo && logoSrc) {
        try {
          const logo = await loadImg(logoSrc);
          const ls = Math.round(Math.min(w, h) * 0.16);
          const pad = Math.round(Math.min(w, h) * 0.04);
          const lr = logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : 1;
          const lw = ls * (lr >= 1 ? 1 : lr);
          const lh = ls * (lr >= 1 ? 1 / lr : 1);
          const x = logoCorner.includes("right") ? w - lw - pad : pad;
          const y = logoCorner.includes("bottom") ? h - lh - pad : pad;
          // Blend the logo directly onto the photo (no badge/frame) with a soft shape shadow.
          ctx.save();
          ctx.shadowColor = "rgba(0,0,0,0.35)";
          ctx.shadowBlur = Math.round(ls * 0.12);
          ctx.shadowOffsetY = 2;
          ctx.drawImage(logo, x, y, lw, lh);
          ctx.restore();
        } catch (e) {}
      }

      if (text && showName) {
        const fs = parseInt(size);
        // Readability scrim behind the text band (keeps the rest of the image clear).
        const bandH = Math.round(fs * 2.4);
        let gy;
        if (position === "top") gy = 0;
        else if (position === "center") gy = Math.round(h / 2 - bandH / 2);
        else gy = h - bandH;
        const grad = ctx.createLinearGradient(0, gy, 0, gy + bandH);
        if (position === "top") { grad.addColorStop(0, "rgba(0,0,0,0.55)"); grad.addColorStop(1, "rgba(0,0,0,0)"); }
        else if (position === "bottom") { grad.addColorStop(0, "rgba(0,0,0,0)"); grad.addColorStop(1, "rgba(0,0,0,0.6)"); }
        else { grad.addColorStop(0, "rgba(0,0,0,0)"); grad.addColorStop(0.5, "rgba(0,0,0,0.5)"); grad.addColorStop(1, "rgba(0,0,0,0)"); }
        ctx.fillStyle = grad; ctx.fillRect(0, gy, w, bandH);

        ctx.font = `800 ${fs}px Tajawal, Cairo, sans-serif`;
        ctx.direction = "rtl";
        ctx.textAlign = "center";
        ctx.fillStyle = color;
        ctx.shadowColor = "rgba(0,0,0,0.65)";
        ctx.shadowBlur = Math.round(fs * 0.25);
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2;
        const pad = Math.round(h * 0.06);
        let y;
        if (position === "top") { ctx.textBaseline = "top"; y = pad; }
        else if (position === "center") { ctx.textBaseline = "middle"; y = h / 2; }
        else { ctx.textBaseline = "bottom"; y = h - pad; }
        ctx.fillText(text, w / 2, y, w * 0.9);
        ctx.shadowColor = "transparent";
      }
    } finally {
      setDrawing(false);
    }
  }, [open, item, size, color, text, position, showLogo, showName, logoSrc, logoCorner]);

  useEffect(() => { redraw(); }, [redraw]);

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setLogoSrc(reader.result); setShowLogo(true); };
    reader.readAsDataURL(file);
  };

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    downloadFile(canvas.toDataURL("image/png"), `${fileName}.png`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-popover border-border max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="image-editor-dialog">
        <DialogHeader>
          <DialogTitle>تحرير الصورة قبل التنزيل</DialogTitle>
          <DialogDescription className="text-muted-foreground">أضِف اسم العلامة والشعار على التصميم ثم نزّله.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 relative">
            {drawing && <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-xl z-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
            <canvas ref={canvasRef} className="w-full rounded-xl border border-border bg-secondary" data-testid="editor-canvas" />
          </div>
          <div className="md:col-span-2 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>نص العلامة / الاسم</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">إظهار الاسم</span>
                  <Switch checked={showName} onCheckedChange={setShowName} data-testid="editor-name-toggle" />
                </div>
              </div>
              <Input value={text} onChange={(e) => setText(e.target.value)} disabled={!showName} className="bg-secondary border-border" data-testid="editor-text-input" />
              {suggestedTexts.length > 0 && (
                <div className="mt-2" data-testid="editor-suggested-texts">
                  <div className="text-[11px] text-muted-foreground mb-1.5">من الخطة الاستراتيجية (انقر للاستخدام):</div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedTexts.map((t, i) => (
                      <button key={i} type="button" onClick={() => setText(t.value)}
                        data-testid={`suggested-text-${i}`}
                        className="text-[11px] bg-secondary hover:bg-primary/15 hover:text-primary border border-border px-2.5 py-1 rounded-full transition-colors max-w-[180px] truncate">
                        {t.label}: {t.value}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-2 block">الموضع</Label>
                <Select value={position} onValueChange={setPosition}>
                  <SelectTrigger className="bg-secondary border-border" data-testid="editor-position-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="top">أعلى</SelectItem>
                    <SelectItem value="center">الوسط</SelectItem>
                    <SelectItem value="bottom">أسفل</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">حجم الخط</Label>
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger className="bg-secondary border-border" data-testid="editor-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {["36", "48", "56", "72", "96"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">لون النص</Label>
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="editor-color-input" className="w-full h-10 rounded-lg bg-transparent border border-border cursor-pointer" />
            </div>
            {allowLogo && (
            <div className="flex items-center justify-between rounded-xl bg-secondary px-3 py-2.5 border border-border">
              <Label className="cursor-pointer">إظهار الشعار</Label>
              <Switch checked={showLogo} onCheckedChange={setShowLogo} data-testid="editor-logo-toggle" />
            </div>
            )}
            {allowLogo && showLogo && (
              <>
                {!logoSrc && (
                  <p className="text-[11px] text-accent" data-testid="logo-hint">لا يوجد شعار معتمد للمشروع بعد — ولّد لوقو واعتمده أو أضفه من «هوية العلامة» ليظهر هنا.</p>
                )}
                <div>
                  <Label className="mb-2 block">زاوية الشعار</Label>
                  <Select value={logoCorner} onValueChange={setLogoCorner}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="editor-logo-corner-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="top-right">أعلى اليمين</SelectItem>
                      <SelectItem value="top-left">أعلى اليسار</SelectItem>
                      <SelectItem value="bottom-right">أسفل اليمين</SelectItem>
                      <SelectItem value="bottom-left">أسفل اليسار</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="cursor-pointer block">
                  <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} data-testid="editor-logo-input" />
                  <div className="flex items-center gap-2 justify-center rounded-xl border border-dashed border-border bg-secondary py-2.5 text-sm text-muted-foreground hover:border-primary/40">
                    <Upload className="w-4 h-4" /> {logoSrc ? "تغيير الشعار" : "رفع شعار"}
                  </div>
                </label>
              </>
            )}
            <Button onClick={download} className="w-full bg-primary hover:bg-primary/90 text-white gap-2" data-testid="editor-download-btn">
              <Download className="w-4 h-4" /> تنزيل الصورة النهائية
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
